import { useTranslation } from 'react-i18next';

export interface PaymentApplicationFormValue {
  invoice_id: string;
  amount_applied: string; // bigint minor units as string
}

export interface OpenInvoice {
  id: string;
  number: string;
  entry_date: string;
  grand_total: string;
  paid_amount: string;
}

export interface PaymentApplicationRowProps {
  index: number;
  value: PaymentApplicationFormValue;
  openInvoices: OpenInvoice[];
  canRemove: boolean;
  onChange: (v: PaymentApplicationFormValue) => void;
  onRemove: () => void;
}

export function PaymentApplicationRow({
  value,
  openInvoices,
  canRemove,
  onChange,
  onRemove,
}: PaymentApplicationRowProps) {
  const { t } = useTranslation('bookkeeping');

  const selectedInvoice = openInvoices.find((inv) => inv.id === value.invoice_id);

  // Compute remaining balance on the selected invoice after applying this amount
  let remaining = '—';
  if (selectedInvoice) {
    try {
      const grandTotal = BigInt(selectedInvoice.grand_total);
      const paidAmount = BigInt(selectedInvoice.paid_amount);
      const openBalance = grandTotal - paidAmount;
      const applied = value.amount_applied ? BigInt(value.amount_applied) : 0n;
      remaining = (openBalance - applied).toString();
    } catch {
      remaining = '—';
    }
  }

  function handleInvoiceChange(invoiceId: string) {
    const invoice = openInvoices.find((inv) => inv.id === invoiceId);
    if (invoice) {
      // Default amount_applied = open balance
      let openBalance = '';
      try {
        openBalance = (BigInt(invoice.grand_total) - BigInt(invoice.paid_amount)).toString();
      } catch {
        openBalance = '';
      }
      onChange({ ...value, invoice_id: invoiceId, amount_applied: openBalance });
    } else {
      onChange({ ...value, invoice_id: invoiceId, amount_applied: '' });
    }
  }

  return (
    <tr className="border-b">
      {/* Invoice picker */}
      <td className="px-2 py-1">
        <select
          value={value.invoice_id}
          onChange={(e) => handleInvoiceChange(e.target.value)}
          className="w-full min-w-40 rounded border px-1 py-0.5 text-sm"
        >
          <option value="">— {t('sales.invoices.title')}</option>
          {openInvoices.map((inv) => (
            <option key={inv.id} value={inv.id}>
              {inv.number} ({inv.entry_date})
            </option>
          ))}
        </select>
      </td>
      {/* Open balance */}
      <td className="px-2 py-1 text-right font-mono text-xs text-slate-500">
        {selectedInvoice
          ? (() => {
              try {
                return (
                  BigInt(selectedInvoice.grand_total) - BigInt(selectedInvoice.paid_amount)
                ).toString();
              } catch {
                return '—';
              }
            })()
          : '—'}
      </td>
      {/* Amount applied */}
      <td className="px-2 py-1">
        <input
          value={value.amount_applied}
          onChange={(e) => onChange({ ...value, amount_applied: e.target.value })}
          placeholder="0"
          className="w-28 rounded border px-1 py-0.5 text-sm"
        />
      </td>
      {/* Remaining after application */}
      <td className="px-2 py-1 text-right font-mono text-xs text-slate-500">{remaining}</td>
      {/* Remove */}
      <td className="px-2 py-1">
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          className="text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-30"
          title="Eliminar aplicación"
        >
          ×
        </button>
      </td>
    </tr>
  );
}
